/**
 * videoViewLayout.js · page sizes for the video pages (the VideoView* pages; punchlist 2.12).
 *
 * Sizes follow the page's CONTENT AREA (its own width after the app's sidebar), measured with
 * ResizeObserver, not the window. The boundaries were chosen so the approved window sizes come out
 * the same with the sidebar in its default state (open from 1,024 px):
 *
 *   wide       1,156 px and up   (windows of 1,436 px and up)   video and the 380 px navigator column
 *   standard   900–1,155 px      (1,180–1,435 px)               one column, ribbon
 *   compact    744–899 px        (1,024–1,179 px, and 744–899)  one column, 36 px gutters, ribbon
 *   small      under 744 px      (under 744 px)                 phone shape, Course button
 *
 *   Compact starts at 744 px, not 768: a 1,024 px window with the sidebar open leaves 744 px of content, and
 *   the approved table makes that window Compact. So windows of 744–767 px (no sidebar) get Compact too.
 *
 *   short      a viewport under 760 px tall, at any width: the video is narrowed so its control bar
 *              stays on screen
 *
 *   The video cap (useVideoCap, 2.12c) goes further, at every height: the video's width is capped so its
 *   bottom, control bar included, stays 16 px above the bottom of the window. It has no effect when there's
 *   room, and never caps below 480 px wide, where the player's controls would turn small.
 *
 * AppLayout keeps its own menu button, sidebar overlay and collapse; it isn't changed.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export const SIZE_WIDE = 1156;
export const SIZE_STANDARD = 900;
export const SIZE_COMPACT = 744;
export const SHORT_HEIGHT = 760;

export function pageSize(contentWidth) {
  if (contentWidth >= SIZE_WIDE) return 'wide';
  if (contentWidth >= SIZE_STANDARD) return 'standard';
  if (contentWidth >= SIZE_COMPACT) return 'compact';
  return 'small';
}

export function isShort(viewportHeight) {
  return viewportHeight < SHORT_HEIGHT;
}

// Measure before the browser paints, so a page never shows one frame at the wrong size.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// The page's own width (the element `ref` points at) and the viewport's height, kept up to date.
export function usePageSize(ref) {
  const [box, setBox] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));
  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const width = Math.round(el.getBoundingClientRect().width);
      const height = window.innerHeight;
      setBox((b) => (b.width === width && b.height === height ? b : { width, height }));
    };
    update();
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(el);
    }
    window.addEventListener('resize', update);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [ref]);
  return { width: box.width, height: box.height, size: pageSize(box.width), short: isShort(box.height) };
}

// 2.12c · the video cap: the widest the video can be with its bottom (control bar included) still on screen.
// `ref` is the box the player sits in; `page` comes from usePageSize; `key` re-measures when the lesson changes.
// `reserve` (October 15, 2026): extra height to keep on screen below the video, so the row of links and controls under
// it (Video · Homework · Answer Key Video · Answer Key PDF) is always in view too, not just the video's control bar.
// The desktop and tablet pages pass VIDEO_CAP_TABS, that row's height with its spacing; the phone pages pass nothing.
export const VIDEO_CAP_GAP = 16;
export const VIDEO_CAP_MIN = 480;
export const VIDEO_CAP_TABS = 96;
// The cap depends on where the box's top is, and that moves after the first paint: the course and chapter lines of
// the title arrive with the catalog (or a course is chosen), the webfont lands, a banner shows. So the measurement is
// repeated whenever any element the box sits in changes size (a ResizeObserver on every ancestor: the page, the
// grid cell, #root… one of them grows when something above the box does) and when the fonts finish loading —
// October 15, 2026. Not the body: the app sets `html, body { height: 100% }`, so the body never changes size and an
// observer on it never fires (that was the first version of this, and it missed every case).
export function useVideoCap(ref, page, key, reserve = 0) {
  const [cap, setCap] = useState(null);
  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      // The offset chain, not getBoundingClientRect: the page's entrance animation translates the content for its
      // first 600 ms, and a rect taken then would sit 20 px low. Offsets ignore transforms.
      let top = 0;
      for (let node = el; node; node = node.offsetParent) top += node.offsetTop;
      const width = Math.max(VIDEO_CAP_MIN, Math.floor((page.height - top - VIDEO_CAP_GAP - reserve) * 16 / 9));
      setCap((c) => (c === width ? c : width));
    };
    measure();
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      for (let node = el.parentElement; node; node = node.parentElement) observer.observe(node);
    }
    const fonts = document.fonts;
    if (fonts && fonts.addEventListener) fonts.addEventListener('loadingdone', measure);
    return () => {
      if (observer) observer.disconnect();
      if (fonts && fonts.removeEventListener) fonts.removeEventListener('loadingdone', measure);
    };
  }, [ref, page.width, page.height, key, reserve]);
  return cap;
}

// ── The video fit (October 30, 2026; the desktop pages only) ─────────────────────────────────────────────────────────
// The cap above answers "how wide can the video be with its control bar (and the tabs) on screen". On a window that is
// SHORT FOR ITS WIDTH — a laptop screen, a zoomed browser, macOS "Larger Text", the Dock and a bookmarks bar — that cap
// takes most of the video away, while a window that is simply narrow is fine at every size. So the desktop pages
// measure the shortfall and give height back in two stages, decided by one number:
//
//   ratio = the box width the height allows ÷ the box width the column would give it (its natural width)
//
// measured on the FULL layout (the three-line title stack, the tabs reserved), never on the stage's own result, so a
// stage cannot feed back on itself:
//   ratio ≥ VIDEO_FIT_STAGE_1 (0.85) · stage 0 · the page as it is
//   ratio < 0.85               · stage 1 · the title shows the lesson line only (course and chapter hidden; outside a
//                                          course the stack is one line already, so stage 1 changes nothing there)
//   ratio < VIDEO_FIT_STAGE_2 (0.70) · stage 2 · and the tabs are no longer reserved on screen: the cap keeps the
//                                          control bar in view plus a small row for a chevron; the tabs sit below the
//                                          fold, and the chevron (fixed at the bottom edge) scrolls to them
// The full stack's height is read whenever the full stack is on screen and remembered, so the ratio can be computed
// while a stage has shortened it. A tall window (ratio ≥ 0.85) never sees any of this. The tablet and phone pages
// keep useVideoCap as it is.
export const VIDEO_FIT_STAGE_1 = 0.85;
export const VIDEO_FIT_STAGE_2 = 0.70;
export const VIDEO_FIT_CHEVRON_ROW = 22;   // stage 2 keeps this much under the video for the chevron, instead of the tabs' 96
// `ref` is the box the player sits in; `stackRef` the element that wraps the title stack (the page marks it
// data-title-stack="full" or "lesson" from what it rendered); `page` comes from usePageSize; `key` is the lesson and
// its title lines (a change re-measures the full stack). Returns { cap, stage, chevron } — `chevron`: stage 2 with the
// tabs below the fold, so the page shows the chevron.
export function useVideoFit(ref, stackRef, page, key) {
  const [fit, setFit] = useState({ cap: null, stage: 0, chevron: false, key, width: page.width, settle: 0 });
  const fullStackRef = useRef(null);   // the full title stack's height, remembered while a stage has shortened it
  const settleRef = useRef(true);      // a full stack has just been rendered and must settle (its own fit) before it is read
  // A new lesson (or new title lines: `key` carries them) or a new width changes what the full stack would measure.
  // Then the stage goes back to 0 for one render — never painted: the layout effect below re-measures the full stack
  // and sets the real stage before the browser paints — so the remembered height is always the current title's.
  if (fit.key !== key || fit.width !== page.width) {
    fullStackRef.current = null;
    settleRef.current = true;
    setFit({ cap: fit.cap, stage: 0, chevron: false, key, width: page.width, settle: fit.settle });
  }
  const stage0 = fit.key !== key || fit.width !== page.width;
  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      // The full stack fits its lines to the width in its own layout effect, whose render lands after this one: the
      // first pass after a reset only asks for a second pass (still before paint), which reads the settled stack.
      if (settleRef.current) {
        settleRef.current = false;
        setFit((f) => ({ ...f, settle: f.settle + 1 }));
        return;
      }
      // The offset chain, not getBoundingClientRect: the page's entrance animation translates the content for its
      // first 600 ms, and a rect taken then would sit 20 px low. Offsets ignore transforms.
      let top = 0;
      for (let node = el; node; node = node.offsetParent) top += node.offsetTop;
      const stackEl = stackRef.current;
      const stackHeight = stackEl ? stackEl.offsetHeight : 0;
      // The page marks the wrapper data-title-stack="full" | "lesson" from what it rendered, so the full height is
      // only ever read from a full stack that is really on screen (never between a state change and its render).
      const showsFull = !stackEl || stackEl.getAttribute('data-title-stack') !== 'lesson';
      if (showsFull || fullStackRef.current === null) fullStackRef.current = stackHeight;
      const topFull = top + (fullStackRef.current - stackHeight);
      const natural = el.parentElement ? el.parentElement.clientWidth : 0;
      // The ratio on the full layout; with no column width yet (first layout), the page as it is.
      let stage = 0;
      if (natural > 0) {
        const fullCap = Math.max(0, (page.height - topFull - VIDEO_CAP_GAP - VIDEO_CAP_TABS) * 16 / 9);
        const ratio = fullCap / natural;
        stage = ratio < VIDEO_FIT_STAGE_2 ? 2 : ratio < VIDEO_FIT_STAGE_1 ? 1 : 0;
      }
      // Back to the full stack without a reset (the window grew): its lines are about to be re-fitted too, so the
      // pass that follows this stage change settles first, the same as after a reset.
      if (stage === 0 && !showsFull) settleRef.current = true;
      const reserve = stage === 2 ? VIDEO_FIT_CHEVRON_ROW : VIDEO_CAP_TABS;
      const cap = Math.max(VIDEO_CAP_MIN, Math.floor((page.height - top - VIDEO_CAP_GAP - reserve) * 16 / 9));
      // The chevron: in stage 2, when the tabs' row (VIDEO_CAP_TABS under the video) does not fit on screen, and the
      // chevron's own row does (at the 480 px floor the video itself runs past the fold: then no chevron over it).
      const boxWidth = natural > 0 ? Math.min(cap, natural) : cap;
      const bottom = top + boxWidth * 9 / 16 + VIDEO_CAP_GAP;
      const chevron = stage === 2 && bottom + VIDEO_CAP_TABS > page.height && bottom + VIDEO_FIT_CHEVRON_ROW <= page.height;
      setFit((f) => (f.cap === cap && f.stage === stage && f.chevron === chevron && f.key === key && f.width === page.width ? f : { ...f, cap, stage, chevron, key, width: page.width }));
    };
    measure();
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      for (let node = el.parentElement; node; node = node.parentElement) observer.observe(node);
    }
    const fonts = document.fonts;
    // The webfont re-fits the stack after it lands: settle, then measure.
    const onFonts = () => { settleRef.current = true; measure(); };
    if (fonts && fonts.addEventListener) fonts.addEventListener('loadingdone', onFonts);
    return () => {
      if (observer) observer.disconnect();
      if (fonts && fonts.removeEventListener) fonts.removeEventListener('loadingdone', onFonts);
    };
  }, [ref, stackRef, page.width, page.height, key, fit.stage, fit.settle, stage0]);
  return { cap: fit.cap, stage: stage0 ? 0 : fit.stage, chevron: stage0 ? false : fit.chevron };
}

// 2.12d · an element's height, measured before paint and kept up to date (the spacer under a fixed header).
export function useElementHeight(ref, active, fallback = 0) {
  const [h, setH] = useState(fallback);
  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!active || !el) return undefined;
    const set = () => setH(el.offsetHeight);
    set();
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(set); observer.observe(el); }
    return () => { if (observer) observer.disconnect(); };
  }, [ref, active]);
  return h;
}
